import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import {
  Mail, Upload, Globe, Send, Calendar as CalendarIcon, BarChart3,
  LogOut, Plus, Eye, Trash2, RefreshCw, Users, MousePointerClick,
  MailOpen, Clock, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet,
  ChevronRight, ChevronLeft, Sparkles, LayoutDashboard, History, Settings, Menu, X,
  Minus, PlusCircle, EyeOff, ChevronDown, ChevronUp, Image as ImageIcon, Palette, Share2, Copy, Download, Facebook, Instagram,
  HelpCircle, Maximize2, Minimize2, AlertTriangle, MessageSquare
} from "lucide-react";
import { ImagePicker } from "@/components/ImagePicker";
import { SectionImageManager } from "@/components/SectionImageManager";
import { GettingStartedPage, GettingStartedChecklist, TroubleshootingHelp } from "@/components/GettingStartedGuide";
import { useAuth, API } from "@/contexts/AuthContext";
import { Layout } from "@/components/layout/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NewCampaign = () => {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Form data
  const [name, setName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");

  const handleCreateCampaign = async () => {
    if (!name || !websiteUrl) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      const res = await authAxios.post("/campaigns", { name, website_url: websiteUrl });
      toast.success("Campaign created! Let's customize your email.");
      // Go straight to the campaign editor
      navigate(`/campaign/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create campaign");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-2xl mx-auto" data-testid="new-campaign">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-afa-navy rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-afa-lime" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Create New Email</h1>
          <p className="text-slate-500">Paste a URL and we'll create a beautiful email for you</p>
        </div>

        {/* Getting Started Checklist */}
        <GettingStartedChecklist />

        {/* Create Form */}
        <Card className="bg-white border-slate-100 rounded-2xl shadow-sm">
          <CardContent className="p-8 space-y-6">
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Campaign Name</Label>
              <Input
                data-testid="campaign-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Spring Wine Release, New Blog Post"
                className="h-12 rounded-xl"
              />
              <p className="text-xs text-slate-400 mt-1">This is just for you to remember what this email is about</p>
            </div>
            
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Page URL</Label>
              <div className="relative">
                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  data-testid="website-url-input"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://yoursite.com/blog/spring-wine-release"
                  className="h-12 rounded-xl pl-12"
                />
              </div>
              <p className="text-sm text-slate-500 mt-2">
                Paste the link to the specific page you want to share — like a blog post, event, or announcement
              </p>
            </div>
            
            <Button
              data-testid="create-campaign-btn"
              onClick={handleCreateCampaign}
              disabled={loading || !name || !websiteUrl}
              className="w-full h-14 bg-afa-navy hover:bg-afa-navy-dark rounded-full font-semibold text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> 
                  Creating Your Email...
                </>
              ) : (
                <>
                  Create Email <ChevronRight className="w-5 h-5 ml-1" />
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Tip */}
        <div className="mt-6 flex items-start gap-3 text-sm text-slate-500">
          <HelpCircle className="w-5 h-5 flex-shrink-0 text-afa-teal" />
          <p>
            <strong>Tip:</strong> After creating your email, you can customize the design and add your contact list in the Send tab when you're ready.
          </p>
        </div>
      </div>
    </Layout>
  );
};
// Campaign Detail

export default NewCampaign;
